import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { 
  MessageSquare, Users, Brain, Megaphone, RefreshCw, Zap, 
  Wrench, Package, Plug, Building, Target, Workflow, BarChart3, Bot, Layers,
  Rocket, Play
} from 'lucide-react';
import '../styles/ToolsSection.css';

const ToolsSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredTool, setHoveredTool] = useState(null);

  const categories = [
    { id: 'all', name: 'Todas las Herramientas', icon: <Target size={16} /> },
    { id: 'comunicacion', name: 'Comunicación', icon: <MessageSquare size={16} /> },
    { id: 'ai', name: 'Inteligencia Artificial', icon: <Brain size={16} /> },
    { id: 'automation', name: 'Automatización', icon: <RefreshCw size={16} /> },
    { id: 'analytics', name: 'Gestión & Análisis', icon: <BarChart3 size={16} /> },
    { id: 'platform', name: 'Plataforma', icon: <Building size={16} /> }
  ];

  const tools = [
    {
      id: 1,
      title: 'Bandeja Unificada',
      shortDescription: 'Todos tus canales en un solo lugar',
      icon: <MessageSquare size={24} />,
      category: 'comunicacion',
      features: ['WhatsApp Business', 'Instagram & Facebook', 'TikTok', 'Telegram & SMS', 'Respuestas rápidas']
    },
    {
      id: 2,
      title: 'CRM Inteligente',
      shortDescription: 'Gestión avanzada de contactos',
      icon: <Users size={24} />,
      category: 'analytics',
      features: ['Base de datos unificada', 'Segmentación automática', 'Pipeline visual', 'Reportes avanzados']
    },
    {
      id: 3,
      title: 'IA Conversacional',
      shortDescription: 'Asistente virtual entrenado para tu negocio',
      icon: <Bot size={24} />,
      category: 'ai',
      features: ['Entrenamiento personalizado', 'Respuestas contextuales', 'Derivación inteligente', 'Aprendizaje continuo']
    },
    {
      id: 4,
      title: 'Mensajería Masiva',
      shortDescription: 'Campañas multicanal automatizadas',
      icon: <Megaphone size={24} />,
      category: 'comunicacion',
      features: ['Envío multicanal', 'Segmentación avanzada', 'Personalización automática', 'Programación inteligente']
    },
    {
      id: 5,
      title: 'Automatización Social',
      shortDescription: 'Gestión automática de redes sociales',
      icon: <RefreshCw size={24} />,
      category: 'automation',
      features: ['Comentarios en Facebook e Instagram', 'Automatización de Facebook leads', 'Historias de Instagram', 'Auto-respuestas 24/7']
    },
    {
      id: 6,
      title: 'Triggers Inteligentes',
      shortDescription: 'Automatizaciones por comportamiento',
      icon: <Zap size={24} />,
      category: 'automation',
      features: ['Etiquetado automático', 'Triggers personalizados', 'Segmentación dinámica', 'Flujos de seguimiento']
    },
    {
      id: 7,
      title: 'Kit de Herramientas',
      shortDescription: 'Utilidades avanzadas integradas',
      icon: <Wrench size={24} />,
      category: 'automation',
      features: ['Códigos QR', 'Formularios dinámicos', 'Análisis de imágenes', 'Geolocalización']
    },
    {
      id: 8,
      title: 'Flujos Conversacionales',
      shortDescription: 'Editor visual de experiencias',
      icon: <Workflow size={24} />,
      category: 'ai',
      features: ['Editor drag & drop', 'Lógica condicional', 'A/B Testing', 'Templates de venta']
    },
    {
      id: 9,
      title: 'Catálogos Dinámicos',
      shortDescription: 'Inventarios sincronizados en tiempo real',
      icon: <Package size={24} />,
      category: 'analytics',
      features: ['Sync automático', 'Precios dinámicos', 'Stock en tiempo real', 'Integración e-commerce']
    },
    {
      id: 10,
      title: 'API & Integraciones',
      shortDescription: 'Conecta con cualquier sistema',
      icon: <Plug size={24} />,
      category: 'analytics',
      features: ['API REST completa', 'Webhooks', 'Google Sheets', 'CRMs externos']
    },
    {
      id: 11,
      title: 'Plataforma Multi-Agente',
      shortDescription: 'Usuarios ilimitados por cuenta',
      icon: <Building size={24} />,
      category: 'platform',
      features: ['Usuarios ilimitados', 'Gestión de equipos', 'Administración de clientes', 'Roles y permisos']
    },
    {
      id: 12,
      title: 'Procesamiento Multimedia',
      shortDescription: 'IA para texto, audio, imágenes y archivos',
      icon: <Layers size={24} />,
      category: 'ai',
      features: ['Generación de texto', 'Procesamiento de audio', 'Análisis de imágenes', 'Gestión de archivos y videos']
    }
  ];

  const filteredTools = selectedCategory === 'all' 
    ? tools 
    : tools.filter(tool => tool.category === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section id="tools" className="tools-section section-page" ref={ref}>
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2>Tu <span className="text-gradient">Arsenal Completo</span></h2>
          <p>
            12 herramientas profesionales que trabajan en perfecta sincronía para automatizar y escalar tu negocio
          </p>
        </motion.div>

        {/* Category Filter */}
        <motion.div 
          className="category-filter"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {categories.map((category) => (
            <motion.button
              key={category.id}
              className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{category.name}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Tools Grid */}
        <motion.div 
          className="tools-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <AnimatePresence mode="wait">
            {filteredTools.map((tool, index) => (
              <motion.div
                key={tool.id}
                className="tool-card"
                variants={cardVariants}
                layout
                onHoverStart={() => setHoveredTool(tool.id)}
                onHoverEnd={() => setHoveredTool(null)}
              >
                <div className="tool-card-inner">
                  {/* Tool Header */}
                  <div className="tool-header">
                    <motion.div 
                      className="tool-icon"
                      animate={{
                        scale: hoveredTool === tool.id ? 1.1 : 1,
                        rotate: hoveredTool === tool.id ? 5 : 0
                      }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {tool.icon}
                    </motion.div>
                    <div className="tool-meta">
                      <h3 className="tool-title">{tool.title}</h3>
                      <p className="tool-description">{tool.shortDescription}</p>
                    </div>
                  </div>

                  {/* Tool Features */}
                  <div className="tool-features">
                    {tool.features.map((feature, featureIndex) => (
                      <div 
                        key={featureIndex}
                        className="feature-item"
                      >
                        <span className="feature-bullet">•</span>
                        <span className="feature-text">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Gradient Overlay */}
                <div className="tool-gradient"></div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          className="tools-cta"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
        >
          <div className="cta-content">
            <motion.div 
              className="cta-icon"
              animate={{ 
                rotate: [0, 5, -5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              🚀
            </motion.div>
            <h3>¿Listo para transformar tu negocio?</h3>
            <p>
              Todas estas herramientas están esperándote en Runamatic. 
              Comienza gratis y experimenta el poder de la automatización inteligente.
            </p>
            
            <div className="cta-buttons">
              <motion.a
                href="https://app.runamatic.io/en/login"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Rocket size={20} />
                Empezar Ahora
              </motion.a>
              
              <motion.button
                className="btn btn-outline"
                onClick={() => navigate('/demo')}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Play size={20} />
                Ver Demo
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ToolsSection; 